import { useState, useEffect } from "react";
import { DollarSign, ShoppingBag, TrendingUp, Users, Clock, CheckCircle2, XCircle, Repeat, Loader2, Wallet, Megaphone, Percent, FileCheck2 } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageHeader, StatCard } from "@/components/admin/ui";
import { api } from "@/lib/api";
import { formatGs } from "@/lib/format";

const PERIODS = [
  { key: "hoy", label: "Hoy" },
  { key: "ayer", label: "Ayer" },
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "mes", label: "Este mes" },
  { key: "todo", label: "Todo" },
];

const Dashboard = () => {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/dashboard", { params: { period } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period]);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Resumen general de la operación de Impulso Visual"
        action={
          <div className="flex flex-wrap gap-1.5 bg-white rounded-lg border border-slate-200 p-1">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${period === p.key ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                {p.label}
              </button>
            ))}
          </div>
        }
      />

      {loading || !data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="space-y-6">
          {/* ventas */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Ventas</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Facturación" value={formatGs(data.revenue)} icon={DollarSign} accent="emerald" />
              <StatCard label="Pedidos" value={data.orders_count} icon={ShoppingBag} accent="indigo" />
              <StatCard label="Ticket medio" value={formatGs(data.avg_ticket)} icon={TrendingUp} accent="blue" />
              <StatCard label="Facturación neta" value={formatGs(data.net)} icon={Wallet} accent="pink" hint={`Margen ${data.margin}%`} />
            </div>
          </div>

          {/* estados de pedidos */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatCard label="Pagados" value={data.paid} icon={CheckCircle2} accent="emerald" />
            <StatCard label="Aguardando pago" value={data.waiting} icon={Clock} accent="amber" />
            <StatCard label="Procesando" value={data.processing} icon={Loader2} accent="blue" />
            <StatCard label="Completados" value={data.completed} icon={CheckCircle2} accent="indigo" />
            <StatCard label="Cancelados" value={data.cancelled} icon={XCircle} accent="rose" />
          </div>

          {/* charts */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-sm font-semibold text-slate-800 mb-4">Facturación por día</p>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.revenue_series}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${v / 1000}k`} width={40} />
                  <Tooltip formatter={(v) => formatGs(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2.5} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-sm font-semibold text-slate-800 mb-4">Servicios más vendidos</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.services_series} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} width={90} tickFormatter={(n) => n.split(" ")[0]} />
                  <Tooltip formatter={(v, n) => n === "revenue" ? formatGs(v) : v} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#ec4899" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* financiero */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Financiero</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Costo servicios" value={formatGs(data.cost_services)} icon={Wallet} accent="slate" />
              <StatCard label="Tráfico pago" value={formatGs(data.traffic_total)} icon={Megaphone} accent="amber" />
              <StatCard label="Lucro bruto" value={formatGs(data.gross_profit)} icon={TrendingUp} accent="emerald" />
              <StatCard label="ROI tráfico" value={`${data.roi}x`} icon={Percent} accent="pink" hint={`Costo/cliente ${formatGs(data.cost_per_customer)}`} />
            </div>
          </div>

          {/* clientes y recurrencia */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Clientes y recurrencia</p>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <StatCard label="Clientes" value={data.total_customers} icon={Users} accent="indigo" />
              <StatCard label="Nuevos" value={data.new_customers} icon={Users} accent="blue" />
              <StatCard label="Recurrentes" value={data.recurrent_customers} icon={Repeat} accent="pink" />
              <StatCard label="Suscripciones activas" value={data.active_subs} icon={Repeat} accent="emerald" />
              <StatCard label="MRR estimado" value={formatGs(data.mrr)} icon={DollarSign} accent="emerald" />
            </div>
          </div>

          {data.pending_comprobantes > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800"><strong>{data.pending_comprobantes}</strong> comprobante(s) aguardando análisis.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
